import React, { createContext, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { TicketCustomFieldFormValues } from 'assets/js/pages/campaign-settings/campaign-event/types';
import * as ArchiveCustomFieldDialog from 'components/custom-fields/ArchiveCustomFieldDialog';
import { ACCEPTED_FILE_EXTENSIONS, MAX_FILE_SIZE } from 'components/custom-fields/constants';
import * as Form from 'components/form';
import { CampaignItem, CustomField } from 'types/domain';
import { CustomFieldType } from 'types/filter';

interface CustomFieldFormContextProps {
    pathPrefix: string;
    customField: CustomField | null;
}

const CustomFieldFormContext = createContext<CustomFieldFormContextProps>({
    pathPrefix: '',
    customField: null,
});

const useCustomField = () => {
    const customFieldFormContext = useContext(CustomFieldFormContext);
    const { pathPrefix } = customFieldFormContext;

    const getFormFieldPath = (field: string) =>
        pathPrefix.length > 0 ? `${pathPrefix}.${field}` : field;

    return {
        ...customFieldFormContext,
        getFormFieldPath,
    };
};

interface CustomFieldFormProps
    extends Pick<CustomFieldFormContextProps, 'customField'>,
        Partial<Pick<CustomFieldFormContextProps, 'pathPrefix'>> {
    children: React.ReactNode;
}

const CustomFieldForm = (props: CustomFieldFormProps) => {
    const { children, pathPrefix = '', customField } = props;

    const value = useMemo(() => ({ pathPrefix, customField }), [pathPrefix, customField]);

    return (
        <CustomFieldFormContext.Provider value={value}>{children}</CustomFieldFormContext.Provider>
    );
};

const CustomFieldTitleForm = () => {
    const { control, getValues } = Form.useGivebutterFormContext();
    const { getFormFieldPath, pathPrefix } = useCustomField();
    const customFields = getValues('custom_fields') || [];
    const fieldIndex = parseInt(pathPrefix.split('.').pop() || '0', 10);
    const currentCustomField = customFields[fieldIndex];
    const isCustomFieldFile = currentCustomField?.type === 'file' || getValues().type === 'file';

    return (
        <div className="flex w-full flex-col gap-2">
            <Form.Container>
                <Form.Label name={getFormFieldPath('title')}>Custom field label</Form.Label>
                <Form.TextInputRoot
                    control={control}
                    name={getFormFieldPath('title')}
                    rules={{ required: 'Must provide a title.' }}
                >
                    <Form.TextInputForm />
                </Form.TextInputRoot>
                <Form.UseFormError name={getFormFieldPath('title')} />
            </Form.Container>
            {isCustomFieldFile && (
                <div className="flex flex-col">
                    <span className={'text-sm text-gray-600'}>
                        Maximum upload file size: {MAX_FILE_SIZE}MB
                    </span>
                    <span className={'text-sm text-gray-600'}>
                        Accepted file types: {ACCEPTED_FILE_EXTENSIONS}
                    </span>
                </div>
            )}
        </div>
    );
};

interface CustomFieldDescriptionFormProps {
    children?: React.ReactNode;
}
const CustomFieldDescriptionForm = (props: CustomFieldDescriptionFormProps) => {
    const { children } = props;
    const { control } = Form.useGivebutterFormContext();
    const { getFormFieldPath } = useCustomField();

    return (
        <Form.Container>
            <Form.Label name={getFormFieldPath('description')}>Optional description</Form.Label>
            <Form.Textarea control={control} name={getFormFieldPath('description')} rows={8} />
            <Form.UseFormError name={getFormFieldPath('description')}>
                <Form.ErrorMessage />
                <Form.ErrorValid>{children}</Form.ErrorValid>
            </Form.UseFormError>
        </Form.Container>
    );
};

const CustomFieldShowCreateForm = () => {
    const { control } = Form.useGivebutterFormContext();
    const { getFormFieldPath } = useCustomField();

    return (
        <>
            <Form.Container className={'flex flex-col gap-y-1'}>
                <div className={'flex items-center justify-between'}>
                    <Form.Label name={getFormFieldPath('showCreate')} className={'cursor-pointer'}>
                        Show when creating a new individual contact
                    </Form.Label>
                    <Form.Switch control={control} name={getFormFieldPath('showCreate')} />
                </div>
                <p className={'text-sm text-gray-600'}>
                    When enabled, the custom field will appear on an individual contact.
                </p>
            </Form.Container>
            <Form.Container className={'flex flex-col gap-y-1'}>
                <div className={'flex items-center justify-between'}>
                    <Form.Label
                        name={getFormFieldPath('showCreateCompany')}
                        className={'cursor-pointer'}
                    >
                        Show when creating a new company contact
                    </Form.Label>
                    <Form.Switch control={control} name={getFormFieldPath('showCreateCompany')} />
                </div>
                <p className={'text-sm text-gray-600'}>
                    When enabled, the custom field will appear on a company contact.
                </p>
            </Form.Container>
        </>
    );
};

interface CustomFieldRequiredFormProps {
    children?: React.ReactNode;
}
const CustomFieldRequiredForm = (props: CustomFieldRequiredFormProps) => {
    const { children } = props;
    const { control, watch } = Form.useGivebutterFormContext();
    const { getFormFieldPath } = useCustomField();
    const hideField = watch(getFormFieldPath('hideField'));

    return (
        <Form.Container>
            {children ? (
                <div className={'flex justify-between'}>
                    <div className={'flex flex-col gap-y-1'}>{children}</div>
                    <Form.Switch
                        control={control}
                        name={getFormFieldPath('required')}
                        disabled={hideField}
                    />
                </div>
            ) : (
                <>
                    <div className={'flex items-center justify-between'}>
                        <RequiredLabel>Required</RequiredLabel>
                        <Form.Switch
                            control={control}
                            name={getFormFieldPath('required')}
                            disabled={hideField}
                        />
                    </div>
                    <FieldDescription>
                        Make this field required when purchasing this ticket type.
                    </FieldDescription>
                </>
            )}
        </Form.Container>
    );
};

const CustomFieldAdminOnlyForm = () => {
    const { control, setValue } = Form.useGivebutterFormContext();
    const { getFormFieldPath } = useCustomField();

    return (
        <Form.Container className={'flex flex-col justify-between gap-x-4 gap-y-1'}>
            <div className="flex items-center justify-between">
                <Form.Label name={getFormFieldPath('hideField')} className={'cursor-pointer'}>
                    Hide field
                </Form.Label>
                <Form.Switch
                    control={control}
                    name={getFormFieldPath('hideField')}
                    onCheckedChange={() => {
                        setValue(getFormFieldPath('required'), false);
                    }}
                />
            </div>
            <p className={'max-w-sm text-sm text-gray-600'}>
                Fields will be visible for internal purposes only and not be displayed to
                supporters.
            </p>
        </Form.Container>
    );
};

interface CustomFieldAllTicketsFormProps {
    ticket: CampaignItem | null;
    setSnapshot: (snapshot: TicketCustomFieldFormValues) => void;
}

const CustomFieldAllTicketsForm = (props: CustomFieldAllTicketsFormProps) => {
    const { ticket, setSnapshot } = props;
    const { getValues, setValue } = Form.useGivebutterFormContext();
    const { getFormFieldPath, customField } = useCustomField();
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const allTicketsPath = getFormFieldPath('all_tickets');

    const handleSwitchEnable = () => {
        setValue(allTicketsPath, true);
    };

    const handleSwitchDisable = () => {
        const currentValues = getValues(allTicketsPath);
        setSnapshot(currentValues);
        setShowDialog(true);
    };

    const confirmDisable = () => {
        setValue(allTicketsPath, false);
        setShowDialog(false);
    };

    return (
        <Form.Container className={'flex flex-row justify-between gap-y-1'}>
            <div className={'flex flex-col gap-y-1'}>
                <Form.Label name={allTicketsPath} className={'cursor-pointer'}>
                    Apply to all tickets
                </Form.Label>
                <p className={'max-w-sm text-sm text-gray-600'}>
                    Make this field appear on all tickets in this campaign. This option cannot be
                    switched off once tickets with this field have been purchased.
                </p>
            </div>
            <AlertDialog.Root type="warning" open={showDialog} onOpenChange={setShowDialog}>
                <Form.Switch
                    name={allTicketsPath}
                    onCheckedChange={() => {
                        const currentValue = getValues(allTicketsPath);
                        if (currentValue) {
                            handleSwitchEnable();
                        } else {
                            handleSwitchDisable();
                        }
                    }}
                />
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content>
                        <AlertDialog.Header>
                            <AlertDialog.Icon />
                            <AlertDialog.Close />
                        </AlertDialog.Header>
                        <AlertDialog.Title>
                            Disassociate custom field with all tickets?
                        </AlertDialog.Title>
                        <AlertDialog.Description>
                            When disabled, <b>{customField?.title}</b> will no longer be associated
                            with all tickets and will only apply to <b>{ticket?.name}</b>.
                        </AlertDialog.Description>
                        <AlertDialog.Footer>
                            <AlertDialog.Cancel onClick={e => e.stopPropagation()}>
                                Close
                            </AlertDialog.Cancel>
                            <AlertDialog.Action
                                onClick={e => {
                                    e.stopPropagation();
                                    confirmDisable();
                                }}
                            >
                                Confirm
                            </AlertDialog.Action>
                        </AlertDialog.Footer>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </Form.Container>
    );
};

interface RequiredLabelProps {
    children: React.ReactNode;
}

const RequiredLabel = (props: RequiredLabelProps) => {
    const { children } = props;
    const { getFormFieldPath } = useCustomField();

    return (
        <Form.Label name={getFormFieldPath('required')} className={'cursor-pointer'}>
            {children || 'Required'}
        </Form.Label>
    );
};

interface FieldDescriptionProps {
    children: React.ReactNode;
}

const FieldDescription = (props: FieldDescriptionProps) => {
    const { children } = props;

    return <p className={'text-sm text-gray-600'}>{children}</p>;
};

const CustomFieldArchiveButton = () => {
    const { customField } = useCustomField();
    const history = useHistory();

    if (!customField) {
        return null;
    }

    return (
        <ArchiveCustomFieldDialog.Root customField={customField} onSuccess={() => history.goBack()}>
            <ArchiveCustomFieldDialog.Trigger asChild={true}>
                <Button variant="transparent" color="error" className="p-2 font-medium">
                    Archive
                </Button>
            </ArchiveCustomFieldDialog.Trigger>
        </ArchiveCustomFieldDialog.Root>
    );
};

const toFieldArray = (array: string[]) => array.map(v => ({ value: v }));

const toFlatArray = (array: Array<{ value: string }>) =>
    array.filter(v => v.value).map(v => v.value);

const typeHasAnswers = (type: CustomFieldType) => ['select', 'multiselect', 'radio'].includes(type);

const Root = CustomFieldForm;
const Title = CustomFieldTitleForm;
const Description = CustomFieldDescriptionForm;
const Required = CustomFieldRequiredForm;
const AdminOnly = CustomFieldAdminOnlyForm;
const AllTickets = CustomFieldAllTicketsForm;
const ShowCreate = CustomFieldShowCreateForm;
const ArchiveButton = CustomFieldArchiveButton;

export {
    CustomFieldFormContext,
    //
    CustomFieldForm,
    CustomFieldTitleForm,
    CustomFieldDescriptionForm,
    CustomFieldRequiredForm,
    CustomFieldAdminOnlyForm,
    CustomFieldAllTicketsForm,
    CustomFieldShowCreateForm,
    CustomFieldArchiveButton,
    //
    Root,
    Title,
    Description,
    Required,
    AdminOnly,
    AllTickets,
    RequiredLabel,
    ShowCreate,
    ArchiveButton,
    FieldDescription,
    //
    toFieldArray,
    toFlatArray,
    typeHasAnswers,
    useCustomField,
};
